import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
    formatStatementIdentifier, formatStatementPeriod,
} from "@/lib/statementDisplay";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
// Phase K.4 (2026-08-07) — refund UI + credit-memos card.
import AdminRefundModal from "@/components/AdminRefundModal";
import CreditMemosCard from "@/components/CreditMemosCard";
import {
    FileText, ArrowLeft, Send, CreditCard, XCircle, Loader2,
    AlertTriangle, CheckCircle2, Clock,
} from "lucide-react";

const STATUS_STYLE = {
    draft: "bg-slate-100 text-slate-800",
    sent: "bg-blue-50 text-blue-900",
    processing: "bg-amber-50 text-amber-900",
    paid: "bg-emerald-100 text-emerald-900",
    failed: "bg-red-50 text-red-700",
    void: "bg-slate-200 text-slate-700",
};

function fmtMoney(cents) {
    if (cents == null) return "—";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}

export default function AdminStatementDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [s, setS] = useState(null);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [sendReason, setSendReason] = useState("");
    const [voidReason, setVoidReason] = useState("");
    const [showSendPrompt, setShowSendPrompt] = useState(false);
    const [showVoidPrompt, setShowVoidPrompt] = useState(false);
    const [showChargeConfirm, setShowChargeConfirm] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/statements/${id}`);
            setS(data);
            // Best-effort audit trail load.
            try {
                const auditRes = await api.get(`/admin/audit-events`, {
                    params: { statement_id: id, limit: 100 },
                });
                setAudit(auditRes.data.items || []);
            } catch (_) {
                setAudit([]);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, [id]);

    const canPatch = s?.status === "draft";
    const canSend = s?.status === "draft" && (s?.total_cents || 0) > 0;
    const canCharge = s && ["sent", "failed"].includes(s.status) && (s.total_cents || 0) > 0;
    const canVoid = s && ["draft", "sent", "failed"].includes(s.status);
    // Phase K.4 — refund gating.
    const refundableCents = Math.max(
        0, Number(s?.total_cents || 0) - Number(s?.refunded_amount_cents || 0),
    );
    const canRefund =
        s && ["paid", "partially_refunded"].includes(s.status) && refundableCents > 0;
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [memos, setMemos] = useState([]);

    useEffect(() => {
        if (!id || !s) return;
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/statements/${id}/credit-memos`,
                );
                setMemos(data.memos || []);
            } catch {
                setMemos([]);
            }
        })();
    }, [id, s?.refunded_amount_cents, s?.status]);

    const doSend = async () => {
        if (!sendReason || sendReason.trim().length < 5) {
            toast.error("Send reason must be at least 5 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/statements/${id}/send`, {
                send_reason: sendReason.trim(),
            });
            toast.success("Statement sent.");
            setShowSendPrompt(false);
            setSendReason("");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doCharge = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/statements/${id}/charge`, {});
            toast.success(
                `Charge initiated. PI: ${data.payment_intent_id?.slice(0, 20)}…`
            );
            setShowChargeConfirm(false);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doVoid = async () => {
        if (!voidReason || voidReason.trim().length < 5) {
            toast.error("Void reason must be at least 5 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/statements/${id}/void`, {
                void_reason: voidReason.trim(),
            });
            toast.success("Statement voided. Invoices unlinked.");
            setShowVoidPrompt(false);
            setVoidReason("");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const doRemoveInvoice = async (invId) => {
        if (!window.confirm(`Remove this invoice from the statement?`)) return;
        setBusy(true);
        try {
            await api.patch(`/admin/statements/${id}`, {
                remove_invoice_ids: [invId],
            });
            toast.success("Invoice removed.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (loading && !s) {
        return (
            <DashboardShell>
                <div className="flex items-center justify-center py-24 text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    Loading statement…
                </div>
            </DashboardShell>
        );
    }

    if (!s) {
        return (
            <DashboardShell>
                <div className="text-center py-24 text-slate-500">
                    Statement not found.
                </div>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            <div className="space-y-6" data-testid="admin-statement-detail-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/statements")}>
                        <ArrowLeft className="h-4 w-4 mr-1" /> All Statements
                    </Button>
                </div>

                {/* Summary */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-semibold flex items-center gap-2"
                                    data-testid="statement-detail-title">
                                    <FileText className="h-6 w-6" />
                                    {formatStatementIdentifier(s)}
                                </h1>
                                <div className="text-sm text-slate-600 mt-1">
                                    Customer:{" "}
                                    <Link
                                        to={`/admin/customers/${s.customer_id}`}
                                        className="text-blue-700 hover:underline"
                                        data-testid="statement-detail-customer"
                                    >
                                        {s.customer_name || `${(s.customer_id || "").slice(0, 12)}…`}
                                    </Link>
                                </div>
                                <div className="text-sm text-slate-600 mt-1"
                                     data-testid="statement-detail-period">
                                    Period: {formatStatementPeriod(
                                        s.period_start_date || s.period_start,
                                        s.period_end_date || s.period_end,
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-1 font-mono"
                                     title={s.id}>
                                    id {s.id}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-3xl font-semibold">
                                    {fmtMoney(s.total_cents)}
                                </div>
                                <div className="mt-1">
                                    <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            STATUS_STYLE[s.status] || "bg-slate-100"
                                        }`}
                                        data-testid="statement-status-badge"
                                    >
                                        {s.status}
                                    </span>
                                </div>
                                {s.stripe_payment_intent_id && (
                                    <div className="text-xs text-slate-500 mt-1 font-mono">
                                        PI: {s.stripe_payment_intent_id.slice(0, 20)}…
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Timestamps + failure reason */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            {s.sent_at && (
                                <div>
                                    <div className="text-slate-500">Sent</div>
                                    <div>{s.sent_at.slice(0, 19)}</div>
                                </div>
                            )}
                            {s.paid_at && (
                                <div>
                                    <div className="text-slate-500">Paid</div>
                                    <div className="text-emerald-700">{s.paid_at.slice(0, 19)}</div>
                                </div>
                            )}
                            {s.failed_at && (
                                <div>
                                    <div className="text-slate-500">Failed</div>
                                    <div className="text-red-700">{s.failed_at.slice(0, 19)}</div>
                                </div>
                            )}
                            {s.voided_at && (
                                <div>
                                    <div className="text-slate-500">Voided</div>
                                    <div>{s.voided_at.slice(0, 19)}</div>
                                </div>
                            )}
                        </div>

                        {s.failure_reason && (
                            <div className="mt-3 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-800">
                                <div className="flex items-center gap-2 font-medium">
                                    <AlertTriangle className="h-4 w-4" /> Charge failure
                                </div>
                                <div className="mt-1">{s.failure_reason}</div>
                                {s.failure_code && (
                                    <div className="mt-1 text-xs">Code: {s.failure_code}</div>
                                )}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="mt-6 flex flex-wrap gap-2">
                            {canSend && (
                                <Button
                                    variant="default"
                                    onClick={() => setShowSendPrompt(true)}
                                    disabled={busy}
                                    data-testid="statement-send-btn"
                                >
                                    <Send className="h-4 w-4 mr-1" /> Send to customer
                                </Button>
                            )}
                            {canCharge && (
                                <Button
                                    variant="default"
                                    onClick={() => setShowChargeConfirm(true)}
                                    disabled={busy}
                                    data-testid="statement-charge-btn"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                    <CreditCard className="h-4 w-4 mr-1" />
                                    {s.status === "failed" ? "Retry charge" : "Charge now"}
                                </Button>
                            )}
                            {canVoid && (
                                <Button
                                    variant="outline"
                                    onClick={() => setShowVoidPrompt(true)}
                                    disabled={busy}
                                    data-testid="statement-void-btn"
                                >
                                    <XCircle className="h-4 w-4 mr-1" /> Void
                                </Button>
                            )}
                            {/* Phase K.4 (2026-08-07) — Refund button. */}
                            {canRefund && (
                                <Button
                                    variant="outline"
                                    onClick={() => setShowRefundModal(true)}
                                    disabled={busy}
                                    data-testid="statement-refund-btn"
                                >
                                    Refund
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Phase K.4 (2026-08-07) — Credit memos card. */}
                <CreditMemosCard
                    memos={memos}
                    paidCents={s.total_cents}
                    refundedCents={s.refunded_amount_cents}
                    heading="Credit memos (admin view)"
                />

                {/* Included Invoices */}
                <Card>
                    <CardContent className="pt-6">
                        <h2 className="text-lg font-medium mb-3">
                            Included invoices ({(s.line_summary || []).length})
                        </h2>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2">Invoice #</th>
                                    <th className="text-left px-3 py-2">Description</th>
                                    <th className="text-left px-3 py-2">Service Period</th>
                                    <th className="text-right px-3 py-2">Amount</th>
                                    <th className="text-left px-3 py-2">Flags</th>
                                    {canPatch && <th />}
                                </tr>
                            </thead>
                            <tbody data-testid="statement-line-summary">
                                {(s.line_summary || []).map((l) => (
                                    <tr key={l.invoice_id} className="border-t"
                                        data-testid={`statement-line-${l.invoice_id}`}>
                                        <td className="px-3 py-2 font-mono">
                                            <Link
                                                to={`/admin/invoices/${l.invoice_id}`}
                                                className="text-blue-700 hover:underline"
                                            >
                                                {l.invoice_number || l.invoice_id.slice(0, 8)}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-slate-600">
                                            {l.description || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-slate-500 text-xs">
                                            {l.service_period_start || "—"} → {l.service_period_end || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium">
                                            {fmtMoney(l.amount_cents)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {l.prorated && (
                                                <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-800">
                                                    prorated
                                                </span>
                                            )}
                                        </td>
                                        {canPatch && (
                                            <td className="px-2 py-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => doRemoveInvoice(l.invoice_id)}
                                                    disabled={busy}
                                                    data-testid={`statement-remove-invoice-${l.invoice_id}`}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                <tr className="border-t bg-slate-50 font-medium">
                                    <td colSpan={3} className="px-3 py-2 text-right">Total</td>
                                    <td className="px-3 py-2 text-right">
                                        {fmtMoney(s.total_cents)}
                                    </td>
                                    <td colSpan={canPatch ? 2 : 1} />
                                </tr>
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                {/* Audit trail */}
                {audit.length > 0 && (
                    <Card>
                        <CardContent className="pt-6">
                            <h2 className="text-lg font-medium mb-3 flex items-center gap-2">
                                <Clock className="h-5 w-5" /> Audit trail
                            </h2>
                            <div className="space-y-2 text-sm">
                                {audit.map((e) => (
                                    <div key={e.id} className="flex items-start gap-3 py-1 border-b border-slate-100">
                                        <div className="text-slate-500 text-xs font-mono w-40 shrink-0">
                                            {(e.created_at || "").slice(0, 19)}
                                        </div>
                                        <div>
                                            <div className="font-medium text-slate-700">{e.type}</div>
                                            {e.details && (
                                                <div className="text-xs text-slate-500 font-mono">
                                                    {JSON.stringify(e.details).slice(0, 200)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Send prompt */}
                {showSendPrompt && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                        <Card className="w-full max-w-md" data-testid="send-statement-modal">
                            <CardContent className="pt-6 space-y-3">
                                <h3 className="text-lg font-medium">Send statement</h3>
                                <p className="text-sm text-slate-600">
                                    Email #1 will go to the customer&apos;s billing contact.
                                </p>
                                <Textarea
                                    placeholder="Reason (min 5 chars) — audit trail"
                                    value={sendReason}
                                    onChange={(e) => setSendReason(e.target.value)}
                                    data-testid="send-reason-input"
                                />
                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => setShowSendPrompt(false)}>Cancel</Button>
                                    <Button onClick={doSend} disabled={busy} data-testid="send-reason-submit">
                                        Send
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Charge confirm */}
                {showChargeConfirm && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                        <Card className="w-full max-w-md" data-testid="charge-confirm-modal">
                            <CardContent className="pt-6 space-y-3">
                                <h3 className="text-lg font-medium">Confirm charge</h3>
                                <p className="text-sm text-slate-600">
                                    An off-session ACH PaymentIntent will be minted against
                                    the customer&apos;s verified bank for{" "}
                                    <strong>{fmtMoney(s.total_cents)}</strong>.
                                </p>
                                <p className="text-xs text-slate-500">
                                    Settlement typically takes 3-4 business days.
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => setShowChargeConfirm(false)}>Cancel</Button>
                                    <Button onClick={doCharge} disabled={busy}
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            data-testid="charge-confirm-submit">
                                        {busy ? "Charging…" : "Charge"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Void prompt */}
                {showVoidPrompt && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                        <Card className="w-full max-w-md" data-testid="void-statement-modal">
                            <CardContent className="pt-6 space-y-3">
                                <h3 className="text-lg font-medium">Void statement</h3>
                                <p className="text-sm text-slate-600">
                                    Invoices will be unlinked and eligible for the next statement.
                                </p>
                                <Textarea
                                    placeholder="Reason (min 5 chars)"
                                    value={voidReason}
                                    onChange={(e) => setVoidReason(e.target.value)}
                                    data-testid="void-reason-input"
                                />
                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => setShowVoidPrompt(false)}>Cancel</Button>
                                    <Button variant="destructive" onClick={doVoid} disabled={busy}
                                            data-testid="void-reason-submit">
                                        Void
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>

            {/* Phase K.4 (2026-08-07) — Refund modal. */}
            {s && (
                <AdminRefundModal
                    kind="statement"
                    resource={s}
                    open={showRefundModal}
                    onOpenChange={setShowRefundModal}
                    onRefunded={() => reload()}
                />
            )}
        </DashboardShell>
    );
}
